import React, { createContext, useCallback, useContext, useEffect,
                useRef, useState } from 'react';
import { MonitorUp, MousePointer2, ShieldCheck, X } from 'lucide-react';
import api from '../../services/api';

/**
 * View-only screen share, with consent, for support and training.
 *
 * Mounted once in the shell so both ends of a session are always listening:
 * the person being asked needs the popup wherever they happen to be in the
 * app, and the person asking needs the answer wherever they are.
 *
 * What the design deliberately does NOT contain is an input channel. The
 * viewer sees pixels and can move a pointer; there is no path for a click, a
 * keystroke or a scroll, because none was built. "View only" here is a
 * property of the wiring, not a rule somebody has to keep.
 *
 * Two consents stand between a request and a picture: the popup below, and
 * then the browser's own screen picker — which is the one that decides WHAT
 * is shared. We never get to choose that on somebody's behalf.
 */
const Ctx = createContext({ request: async () => {}, active: null });
export const useScreenShare = () => useContext(Ctx);

const ICE = [{ urls: 'stun:stun.l.google.com:19302' },
             { urls: 'stun:stun1.l.google.com:19302' }];

const wsUrl = () => {
  const token = localStorage.getItem('token') || '';
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws/screen-share?token=${encodeURIComponent(token)}`;
};

export default function ScreenShareProvider({ children }) {
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const [incoming, setIncoming] = useState(null);   // a request awaiting an answer
  const [viewing, setViewing] = useState(null);     // { key, name } — I am watching
  const [sharing, setSharing] = useState(null);     // { key } — my screen is out
  const [pointer, setPointer] = useState(null);     // {x, y} as fractions
  const [tap, setTap] = useState(null);             // {x, y, n} — a marked spot
  const [status, setStatus] = useState('');
  const videoRef = useRef(null);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg));
  }, []);

  const teardown = useCallback(() => {
    try { pcRef.current?.close(); } catch { /* already gone */ }
    pcRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setViewing(null); setSharing(null); setPointer(null); setTap(null);
    setStatus('');
  }, []);

  // ── The sharer's side: accept, pick a screen, send it ────────────────────
  const accept = useCallback(async (req) => {
    setIncoming(null);
    let stream;
    try {
      // The browser's picker. If they cancel here, nothing is shared and the
      // viewer is told — silence would leave them staring at a black frame.
      stream = await navigator.mediaDevices.getDisplayMedia({
        // THE POINTER IS THE POINT.
        //
        // "you should see his screen, his mouse everything"
        //
        // getDisplayMedia defaults to cursor:"auto", which lets the browser
        // leave the pointer out of the captured frames — so the viewer saw a
        // screen where things happened with nothing visibly doing them.
        // Watching somebody work is mostly watching where they are pointing.
        //
        // And 8fps was chosen for bandwidth when nobody expected to follow a
        // cursor with it. 15 is still cheap and makes a pointer readable
        // instead of a sequence of jumps.
        video: { frameRate: 15, cursor: 'always' },
        audio: false,
      });
    } catch (e) {
      send({ type: 'share-error', session_key: req.session_key,
             message: 'They cancelled the screen picker.' });
      try { await api.post(`/api/screen-share/${req.session_key}/end`,
                           { reason: 'picker cancelled' }); } catch { /* logged */ }
      return;
    }
    streamRef.current = stream;
    setSharing({ key: req.session_key, from: req.from_name });
    send({ type: 'accept', session_key: req.session_key });

    const pc = new RTCPeerConnection({ iceServers: ICE });
    pcRef.current = pc;
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: 'ice', session_key: req.session_key,
                              candidate: e.candidate });
    };
    // Ending it from the browser's own "Stop sharing" bar has to end the
    // session too, or the record says it is still running.
    stream.getVideoTracks()[0].addEventListener('ended', () => {
      api.post(`/api/screen-share/${req.session_key}/end`, { reason: 'sharer stopped' })
        .catch(() => {});
      teardown();
    });
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    send({ type: 'offer', session_key: req.session_key, sdp: pc.localDescription });
  }, [send, teardown]);

  const decline = useCallback((req) => {
    send({ type: 'decline', session_key: req.session_key });
    setIncoming(null);
  }, [send]);

  // ── The socket ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!localStorage.getItem('token')) return undefined;
    let closed = false;
    let ws;
    let retry;

    const open = () => {
      try { ws = new WebSocket(wsUrl()); } catch { return; }
      wsRef.current = ws;
      ws.onmessage = async (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === 'request') { setIncoming(m); return; }
        if (m.type === 'declined') {
          setStatus('They declined.'); setViewing(null); return;
        }
        if (m.type === 'ended') { teardown(); return; }
        if (m.type === 'share-error') { setStatus(m.message || 'Nothing was shared.');
                                        setViewing(null); return; }
        if (m.type === 'accepted') {
          setViewing(v => v || { key: m.session_key, name: m.sharer_name || 'Their screen' });
          setStatus('Waiting for their screen…'); return;
        }

        if (m.type === 'offer') {
          // I asked, they accepted: receive only.
          //
          // The window that receives the screen is not necessarily the window
          // that asked — somebody signed in twice gets the offer in both. So
          // the viewer opens from the offer itself rather than from state the
          // asking window happens to hold.
          setViewing(v => v || { key: m.session_key, name: m.sharer_name || 'Their screen' });
          const pc = new RTCPeerConnection({ iceServers: ICE });
          pcRef.current = pc;
          pc.ontrack = (e) => {
            if (videoRef.current) videoRef.current.srcObject = e.streams[0];
            setStatus('');
          };
          pc.onicecandidate = (e) => {
            if (e.candidate) send({ type: 'ice', session_key: m.session_key,
                                    candidate: e.candidate });
          };
          await pc.setRemoteDescription(m.sdp);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: 'answer', session_key: m.session_key, sdp: pc.localDescription });
          return;
        }
        if (m.type === 'answer' && pcRef.current) {
          await pcRef.current.setRemoteDescription(m.sdp); return;
        }
        if (m.type === 'ice' && pcRef.current) {
          try { await pcRef.current.addIceCandidate(m.candidate); } catch { /* late */ }
          return;
        }
        if (m.type === 'pointer') { setPointer({ x: m.x, y: m.y }); return; }
        if (m.type === 'tap') { setTap({ x: m.x, y: m.y, n: m.n || Date.now() }); return; }
      };
      ws.onclose = () => {
        if (!closed) retry = setTimeout(open, 4000);
      };
    };
    open();
    const beat = setInterval(() => send({ type: 'ping' }), 25000);
    return () => {
      closed = true; clearInterval(beat); clearTimeout(retry);
      try { ws && ws.close(); } catch { /* closing */ }
    };
  }, [send, teardown]);

  // ── The viewer's side: ask ───────────────────────────────────────────────
  const request = useCallback(async (userId, { name, reason, clientId } = {}) => {
    setStatus('Asking…');
    const r = await api.post('/api/screen-share/request',
                             { user_id: userId, reason, client_id: clientId });
    setViewing({ key: r.data.session_key, name: name || r.data.sent_to });
    setStatus(`Waiting for ${r.data.sent_to} to accept…`);
    return r.data;
  }, []);

  const endSession = useCallback(async (key) => {
    try { await api.post(`/api/screen-share/${key}/end`, {}); } catch { /* gone */ }
    teardown();
  }, [teardown]);

  // The mark clears itself. A dot that stays for ever stops meaning "here,
  // now" and becomes part of the furniture.
  useEffect(() => {
    if (!tap) return undefined;
    const id = setTimeout(() => setTap(null), 2000);
    return () => clearTimeout(id);
  }, [tap]);

  // The pointer the sharer follows. Throttled: a mousemove fires far more
  // often than anybody can follow, and every one of them is a socket frame.
  const lastSent = useRef(0);
  const onViewerMouse = (e) => {
    if (!viewing) return;
    const now = Date.now();
    if (now - lastSent.current < 60) return;
    lastSent.current = now;
    const r = e.currentTarget.getBoundingClientRect();
    send({ type: 'pointer', session_key: viewing.key,
           x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  };

  // "when i click he sees a dot where i click so he knows where to click"
  //
  // A moving arrow says "about here". On a support call that is not enough:
  // the thing you are pointing at is usually one of several similar things an
  // inch apart, and the arrow has already moved on by the time they look up.
  // A click leaves a mark that stays put and announces itself.
  //
  // IT MARKS, IT DOES NOT CLICK. The person at the machine still does
  // everything — this is pointing at a screen across a desk, not taking the
  // mouse. There is no message in this application that carries a click to
  // another browser, and the relay drops anything not on its list.
  const onViewerTap = (e) => {
    if (!viewing) return;
    const r = e.currentTarget.getBoundingClientRect();
    send({ type: 'tap', session_key: viewing.key,
           x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height,
           n: Date.now() });
  };

  return (
    <Ctx.Provider value={{ request, active: viewing || sharing }}>
      {children}

      {/* Asked to share — the first of the two consents. */}
      {incoming && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4000,
                      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 460, maxWidth: '92vw', background: 'var(--card-bg, #fff)',
                        color: 'var(--text-primary)', borderRadius: 14, padding: 24,
                        border: '1px solid var(--divider, #E5E7EB)',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <MonitorUp size={20} color="#2563eb" />
              <div style={{ fontSize: 17, fontWeight: 800 }}>Share your screen?</div>
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>
              <strong>{incoming.from_name}</strong> is asking to view your screen
              {incoming.reason ? <> — “{incoming.reason}”</> : null}.
            </div>
            <ul style={{ fontSize: 12.5, color: 'var(--text-secondary, #6B7280)',
                         marginTop: 12, paddingLeft: 18, lineHeight: 1.7 }}>
              <li>They can <strong>see</strong> what you share. They cannot click,
                  type or control anything.</li>
              <li>You choose what to share on the next screen, and you can stop
                  at any time.</li>
              <li>The session is recorded — who asked, that you agreed, and how
                  long it lasted.</li>
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={() => decline(incoming)}
                style={{ padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                         border: '1px solid var(--divider, #E5E7EB)', background: 'transparent',
                         color: 'var(--text-primary)', cursor: 'pointer' }}>
                No thanks
              </button>
              <button onClick={() => accept(incoming)}
                style={{ padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                         border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer' }}>
                Share my screen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My screen is being shared. Always visible, never dismissible: nobody
          should be able to forget this is running. */}
      {sharing && (
        <div style={{ position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
                      zIndex: 4000, display: 'flex', alignItems: 'center', gap: 12,
                      background: '#111827', color: '#fff', borderRadius: 999,
                      padding: '9px 16px', boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}>
          <ShieldCheck size={15} color="#22c55e" />
          <span style={{ fontSize: 12.5 }}>
            Sharing your screen with <strong>{sharing.from}</strong> · view only
          </span>
          <button onClick={() => endSession(sharing.key)}
            style={{ border: 'none', background: '#ef4444', color: '#fff', borderRadius: 999,
                     padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            Stop
          </button>
        </div>
      )}

      {/* The arrow they follow. Drawn over this tab — see the note in the
          viewer window about what that does and does not cover. */}
      {/* The marked spot. Bigger than the arrow, and it pulses once — the
          point is to catch an eye that is looking somewhere else. */}
      {sharing && tap && (
        <div key={tap.n}
             style={{ position: 'fixed', left: `${tap.x * 100}%`, top: `${tap.y * 100}%`,
                      zIndex: 4002, pointerEvents: 'none',
                      transform: 'translate(-50%, -50%)' }}>
          <span style={{ display: 'block', width: 26, height: 26, borderRadius: '50%',
                         background: 'rgba(37,99,235,0.85)',
                         boxShadow: '0 0 0 6px rgba(37,99,235,0.25)',
                         animation: 'ss-tap 2s ease-out forwards' }} />
          <style>{`@keyframes ss-tap{
            0%{transform:scale(0.4);opacity:0}
            12%{transform:scale(1.25);opacity:1}
            30%{transform:scale(1);opacity:1}
            100%{transform:scale(1);opacity:0}}`}</style>
        </div>
      )}

      {sharing && pointer && (
        <div style={{ position: 'fixed', left: `${pointer.x * 100}%`, top: `${pointer.y * 100}%`,
                      zIndex: 4001, pointerEvents: 'none', transform: 'translate(-2px, -2px)',
                      transition: 'left 80ms linear, top 80ms linear' }}>
          <MousePointer2 size={30} color="#2563eb" fill="#2563eb" strokeWidth={1}
                         style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))' }} />
          <span style={{ marginLeft: 6, background: '#2563eb', color: '#fff', fontSize: 11,
                         fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>
            {sharing.from}
          </span>
        </div>
      )}

      {/* What I am watching. */}
      {viewing && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.82)',
                      display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px',
                        color: '#fff' }}>
            <MonitorUp size={17} />
            <div style={{ fontWeight: 700 }}>{viewing.name}</div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              View only — move your mouse over the picture and they see an arrow.
            </div>
            <button onClick={() => endSession(viewing.key)}
              style={{ marginLeft: 'auto', border: 'none', background: '#ef4444', color: '#fff',
                       borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700,
                       cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <X size={13} /> End session
            </button>
          </div>
          <div onMouseMove={onViewerMouse} onClick={onViewerTap}
               title="Click to put a marker on their screen — it does not click for them"
               style={{ flex: 1, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', padding: '0 18px 18px', cursor: 'crosshair' }}>
            {status ? (
              <div style={{ color: '#fff', fontSize: 14 }}>{status}</div>
            ) : null}
            <video ref={videoRef} autoPlay playsInline muted
                   style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10,
                            display: status ? 'none' : 'block',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} />
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
